import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useInventory } from "../hooks/useInventory";
import { useRole } from "@/hooks/use-role";
import { useLocation } from "@/contexts/LocationContext";
import { StockWriteLocationItems } from "@/features/locations/components/StockWriteLocationItems";
import { toast } from "@/hooks/use-toast";

const optionalMoney = z.preprocess(
  (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
  z.number().nonnegative("Cost must be 0 or higher").optional()
);

// Define validation schema for the form
const skuSchema = z.object({
  sku: z.string().min(3, "SKU must be at least 3 characters"),
  category: z.string().min(1, "Category is required"),
  color: z.string().optional(),
  size: z.string().optional(),
  stockLevel: z.coerce.number().int().nonnegative("Stock level must be 0 or higher").default(0),
  // TRIM is a UI-level choice: it submits as isTrim=true and the server
  // stores production_type='PAP' + is_trim (BLU-534).
  productionType: z.enum(["POD", "TRIM"]),
  skuCostOverride: optionalMoney,
});

type SkuFormValues = z.infer<typeof skuSchema>;

interface AddSkuModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProductAdded: () => void;
  /** Production type to preselect when the modal opens (e.g. 'TRIM' when opened
   *  from the Trims tab). Defaults to 'POD'. */
  defaultProductionType?: SkuFormValues['productionType'];
}

const AddSkuModal: React.FC<AddSkuModalProps> = ({
  isOpen,
  onClose,
  onProductAdded,
  defaultProductionType = 'POD',
}) => {
  const { t } = useTranslation('inventory');
  const { addSku } = useInventory();
  // `allLocations`: creating a SKU seeds an inventory row, which is a stock
  // write and may legitimately target a delegated location.
  const { selectedLocation, allLocations } = useLocation();
  const { can } = useRole();
  // The Default cost input needs skus.costs_manage; the server 403s a cost write without it.
  const canManageCosts = can('skus.costs_manage');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [targetLocationId, setTargetLocationId] = useState<string>('');

  useEffect(() => {
    if (!isOpen) return;
    setTargetLocationId(selectedLocation?.id ?? '');
  }, [isOpen, selectedLocation?.id]);

  // Set up the form with Zod validation
  const form = useForm<SkuFormValues>({
    resolver: zodResolver(skuSchema),
    defaultValues: {
      sku: "",
      category: "",
      color: "",
      size: "",
      stockLevel: 0,
      productionType: defaultProductionType,
      skuCostOverride: undefined,
    },
  });

  // Re-seed the production type each time the modal opens, so opening from the
  // Trims tab preselects TRIM even after a prior POD add (react-hook-form keeps
  // defaultValues from first mount otherwise).
  useEffect(() => {
    if (isOpen) form.setValue('productionType', defaultProductionType);
  }, [isOpen, defaultProductionType, form]);

  const handleSubmit = async (data: SkuFormValues) => {
    try {
      setIsSubmitting(true);
      const locationId = selectedLocation?.id ?? targetLocationId;
      if (!locationId) {
        toast({
          title: "Add SKU Failed",
          description: "Select a location first (top right) or choose one in this modal.",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      await addSku({
        locationId,
        sku: data.sku,
        category: {
          type: data.category,
          color: data.color || undefined,
          size: data.size || undefined,
        },
        stockLevel: data.stockLevel,
        // Trims store PAP semantics server-side; the isTrim flag is what matters.
        productionType: data.productionType === 'TRIM' ? 'PAP' : data.productionType,
        isTrim: data.productionType === 'TRIM',
        skuCostOverride: data.skuCostOverride,
      });
      
      setIsSubmitting(false);
      form.reset();
      onProductAdded();
      onClose();
    } catch (_error) {
      setIsSubmitting(false);
      // Error is handled by the mutation's onError callback
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {defaultProductionType === 'TRIM' ? 'Add new trim' : 'Add new SKU'}
          </DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {!selectedLocation && (
              <div className="space-y-2">
                <Label>Location*</Label>
                <Select value={targetLocationId} onValueChange={setTargetLocationId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a location" />
                  </SelectTrigger>
                  <SelectContent>
                    <StockWriteLocationItems locations={allLocations} />
                  </SelectContent>
                </Select>
              </div>
            )}

            <FormField
              control={form.control}
              name="sku"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>SKU*</FormLabel>
                  <FormControl>
                    <Input placeholder="SKU identifier" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category*</FormLabel>
                  <FormControl>
                    <Input placeholder="T-Shirt, Hoodie, etc." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="color"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Color</FormLabel>
                    <FormControl>
                      <Input placeholder="Red, Blue, etc." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            
              <FormField
                control={form.control}
                name="size"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Size</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="XS">XS</SelectItem>
                        <SelectItem value="S">S</SelectItem>
                        <SelectItem value="M">M</SelectItem>
                        <SelectItem value="L">L</SelectItem>
                        <SelectItem value="XL">XL</SelectItem>
                        <SelectItem value="2X">2X</SelectItem>
                        <SelectItem value="3X">3X</SelectItem>
                        <SelectItem value="4X">4X</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="productionType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Production Type*</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select production type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="POD">Print on Demand (POD)</SelectItem>
                      <SelectItem value="TRIM">Trim / Component</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="stockLevel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Initial Stock Level</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {canManageCosts && (
                <FormField
                  control={form.control}
                  name="skuCostOverride"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('addSkuModal.costOverrideLabel')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Optional"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
            
            <DialogFooter className="pt-4">
              <Button 
                variant="outline" 
                onClick={onClose}
                type="button"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-threadheads-yellow text-black hover:bg-threadheads-yellow/90"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Adding..." : "Add SKU"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddSkuModal;
